import { Component, OnInit, ChangeDetectorRef, OnDestroy, HostListener } from '@angular/core';
import { PatientLocation, PatientLocationService } from '../../services/patient-location.service';
import { environment } from 'src/environments/environment';
import {
  setupMapboxGL,
  createMap,
  createNavigationControl,
  createMarker,
  createPopup,
  createLngLatBounds
} from './mapbox-gl-workaround';
import * as mapboxgl from 'mapbox-gl';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

// Interface local para substituir MatSlideToggleChange
interface SlideToggleEvent {
  checked: boolean;
}

// Interface para os itens da lista de localizações
interface LocationListItem {
  id: string;
  name: string;
  count: number;
  visible: boolean;
  originalLocation: PatientLocation;
}

@Component({
  selector: 'app-patient-map',
  templateUrl: './patient-map.component.html',
  styleUrls: ['./patient-map.component.scss']
})
export class PatientMapComponent implements OnInit, OnDestroy {
  mapboxToken: string = environment.mapboxToken;
  map: any = null;
  style = 'mapbox://styles/mapbox/light-v10';
  
  // Coordenadas iniciais (Brasil)
  lat = -15.7801;
  lng = -47.9292;
  zoom = 4;

  // Armazenamento de localizações
  locations: PatientLocation[] = [];
  visibleLocations: PatientLocation[] = [];
  
  // Lista para o UI
  locationsList: LocationListItem[] = [];
  filteredLocationsList: LocationListItem[] = [];
  
  // Busca
  searchTerm: string = '';
  private searchSubject = new Subject<string>();
  
  // Estado da UI
  isLoading: boolean = true;
  error: string | null = null;
  selectedLocation: PatientLocation | null = null;
  
  // Controle de responsividade
  isMobileView: boolean = false;
  showFilters: boolean = true;
  
  // Cache de marcadores para otimização (não usado mais com clustering)
  markers: { [key: string]: any } = {};
  visibleMarkers: any[] = [];
  
  // Controle de clustering
  private clustersInitialized: boolean = false;
  
  // Controle de popup ativo
  private currentPopup: any = null;
  
  // Para limpeza de observables
  private destroy$ = new Subject<void>();

  constructor(
    private patientLocationService: PatientLocationService,
    private cdr: ChangeDetectorRef
  ) { 
    this.checkScreenSize();
  }

  // Detectar mudanças no tamanho da tela
  @HostListener('window:resize', ['$event'])
  onResize() {
    this.checkScreenSize();
  }
  
  // Verificar se é uma visualização mobile
  checkScreenSize() {
    this.isMobileView = window.innerWidth < 768;
    // Em telas pequenas, começar com o filtro escondido
    if (this.isMobileView) {
      this.showFilters = false;
    } else {
      this.showFilters = true;
    }
  }
  
  // Alternar exibição do painel de filtros
  toggleFiltersPanel() {
    this.showFilters = !this.showFilters;
    
    // Redimensionar o mapa após alternar a visibilidade dos filtros
    setTimeout(() => {
      if (this.map) {
        this.map.resize();
        
        // Reajustar os limites do mapa para mostrar todos os marcadores
        if (this.visibleLocations.length > 0) {
          const bounds = createLngLatBounds();
          
          this.visibleLocations.forEach(location => {
            if (location.latitude && location.longitude) {
              bounds.extend([location.longitude, location.latitude]);
            }
          });
          
          if (!bounds.isEmpty()) {
            this.map.fitBounds(bounds, {
              padding: 50,
              duration: 500,
              maxZoom: 9
            });
          }
        }
      }
    }, 300); // Aguardar a animação de exibição/ocultação do painel
  }

  ngOnInit() {
    // Configurar debounce para a busca
    this.searchSubject.pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.filterLocations();
    });
    
    this.fetchLocations();
    
    // Verificar tamanho da tela e ajustar o mapa quando a orientação mudar
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        this.checkScreenSize();
        if (this.map) {
          this.map.resize();
          this.updateMapWithClustering();
        }
      }, 200);
    });
  }
  
  ngOnDestroy() {
    // Limpar todos os observables
    this.destroy$.next();
    this.destroy$.complete();
    
    // Fechar popup ativo se houver
    this.closeCurrentPopup();
    
    // Limpar referências ao mapa e marcadores
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
    
    this.clearMarkers();
  }

  fetchLocations() {
    this.isLoading = true;
    this.error = null;
    
 
    
    this.patientLocationService.getLocations().subscribe({
      next: (data) => {
       
        
        // Validação dos dados recebidos
        if (!data || !Array.isArray(data)) {
          this.error = 'Dados inválidos recebidos da API';
          this.isLoading = false;
          return;
        }

        // Validação de cada localização - aceitar registros com coordenadas válidas
        // e número de pacientes, mesmo se outros campos estiverem faltando
        this.locations = data.filter(location =>
          // Verificar apenas campos essenciais para exibição no mapa
          typeof location.latitude === 'number' &&
          typeof location.longitude === 'number' &&
          typeof location.patientCount === 'number' &&
          location.patientCount > 0 // Garantir que tenha pelo menos 1 paciente
        );

        // Preparar lista de localizações para o filtro lateral
        this.prepareLocationsList();
        this.visibleLocations = [...this.locations];

        this.isLoading = false;

        // Inicializar o mapa com um pequeno atraso para garantir que o elemento DOM esteja pronto
        // Mesmo sem pacientes, o mapa base é renderizado para que a estrutura (filtros + legenda)
        // continue visível — útil para demonstrações em clínicas novas.
        setTimeout(() => {
          this.initializeMap();
          this.cdr.detectChanges();
        }, 100);
      },
      error: (error) => {
        console.error('Erro ao buscar localizações:', error);
        this.error = 'Erro ao carregar dados do mapa. Por favor, tente novamente.';
        this.isLoading = false;
      }
    });
  }
  
  /**
   * Prepara a lista de localizações para o painel lateral
   */
  prepareLocationsList() {
    // Criar uma mapa para agrupar localizações por cidade/estado
    const locationMap = new Map<string, LocationListItem>();
    
    this.locations.forEach(loc => {
      // Definir um identificador único e um nome para exibição
      const locationId = this.getLocationId(loc);
      const locationName = this.getLocationName(loc);
      
      if (!locationMap.has(locationId)) {
        locationMap.set(locationId, {
          id: locationId,
          name: locationName,
          count: loc.patientCount,
          visible: true, // Iniciar como visível
          originalLocation: loc
        });
      } else {
        // Se já existe esta localização, só incrementa o contador
        const existing = locationMap.get(locationId)!;
        existing.count += loc.patientCount;
      }
    });
    
    // Converter mapa para array e ordenar por nome
    this.locationsList = Array.from(locationMap.values())
      .sort((a, b) => a.name.localeCompare(b.name));
    
    // Iniciar com todos os itens visíveis e filtrados
    this.filteredLocationsList = [...this.locationsList];
  }
  
  /**
   * Gera um ID único para uma localização baseado na cidade e estado
   */
  private getLocationId(location: PatientLocation): string {
    return `${location.city || 'unknown'}_${location.state || 'unknown'}`;
  }
  
  /**
   * Gera um nome para exibição na lista de filtros
   */
  private getLocationName(location: PatientLocation): string {
    if (location.city && location.state) {
      return `${location.city}, ${location.state}`;
    } else if (location.city) {
      return location.city;
    } else if (location.state) {
      return location.state;
    } else if (location.country) {
      return `${location.country} (local desconhecido)`;
    }
    return 'Localização desconhecida';
  }

  /**
   * Filtra as localizações com base no termo de busca
   */
  filterLocations() {
    const term = this.searchTerm.trim().toLowerCase();
    
    if (!term) {
      this.filteredLocationsList = [...this.locationsList];
    } else {
      this.filteredLocationsList = this.locationsList.filter(loc => 
        loc.name.toLowerCase().includes(term)
      );
    }
  }
  
  /**
   * Atualiza as localizações visíveis com base nas seleções do usuário
   */
  updateVisibleLocations() {
    // Obter IDs das localizações que devem ser visíveis
    const visibleIds = new Set(
      this.locationsList
        .filter(loc => loc.visible)
        .map(loc => this.getLocationId(loc.originalLocation))
    );
    
    // Filtrar as localizações baseado na visibilidade
    this.visibleLocations = this.locations.filter(loc => 
      visibleIds.has(this.getLocationId(loc))
    );
    
    // Atualizar o mapa com clustering
    this.updateMapWithClustering();
  }
  
  /**
   * Marca ou desmarca todas as localizações
   */
  toggleAllLocations(allVisible: boolean) {
    // Atualizar todas as localizações na lista completa
    this.locationsList.forEach(loc => loc.visible = allVisible);
    
    // Atualizar a lista filtrada também
    this.filteredLocationsList.forEach(loc => loc.visible = allVisible);

    // Atualizar o mapa
      this.updateVisibleLocations();
  }

  initializeMap() {
    // Verificar se já foi inicializado
    if (this.map) {
      this.map.resize(); // Atualizar o tamanho do mapa para ajustar ao contêiner
      this.updateMapWithClustering();
      return;
    }
    
    if (!this.mapboxToken) {
      this.error = 'Token do Mapbox não configurado';
      return;
    }
    
    try {
      // Configurar o token do Mapbox
      setupMapboxGL(this.mapboxToken);
      
      // Criar o mapa
      this.map = createMap('map', {
        style: this.style,
        center: [this.lng, this.lat],
        zoom: this.zoom,
        minZoom: 3, // Impedir zoom out excessivo
        maxZoom: 15, // Limitar o zoom máximo
        attributionControl: false, // Remover a atribuição padrão
        pitchWithRotate: false // Desabilitar inclinação do mapa
      });
      
      // Adicionar controles de navegação
      this.map.addControl(
        createNavigationControl(), 
        'top-right'
      );
      
      // Após carregar o mapa
      this.map.on('load', () => {
        this.initializeClustering();
        this.updateMapWithClustering();
        
        // Verificar se o contêiner do mapa tem altura
        const mapContainer = document.getElementById('map');
        if (mapContainer && mapContainer.clientHeight < 50) {
          console.warn('Contêiner do mapa tem altura muito pequena, forçando resize');
          mapContainer.style.height = '350px';
          setTimeout(() => {
            this.map.resize();
          }, 100);
        }
      });

      // Adicionar evento para fechar popup ao clicar no mapa (fora dos marcadores)
      this.map.on('click', (e: any) => {
        // Verificar se o clique foi em um marcador
        const features = this.map.queryRenderedFeatures(e.point, {
          layers: ['clusters', 'unclustered-point']
        });
        
        // Se não clicou em nenhum marcador, fechar popup
        if (features.length === 0) {
          this.closeCurrentPopup();
        }
      });
      
      // Ajustar marcadores para dispositivos móveis
      if (this.isMobileView) {
        this.map.setMinZoom(4); // Aumentar o zoom mínimo em telas pequenas
      }
    } catch (error) {
      console.error('Erro ao inicializar o mapa:', error);
      this.error = 'Não foi possível inicializar o mapa. Verifique a conexão e tente novamente.';
    }
  }
  
  /**
   * Inicializa o sistema de clustering
   */
  private initializeClustering() {
    if (!this.map || this.clustersInitialized) return;

    // Adicionar fonte de dados vazios para os clusters
    this.map.addSource('patients', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: []
      },
      cluster: true,
      clusterMaxZoom: 14, // Máximo zoom para clustering
      clusterRadius: 50, // Raio do cluster em pixels
      clusterProperties: {
        // Propriedades agregadas para os clusters
        'sum_patients': ['+', ['get', 'patientCount']]
      }
    });

    // Camada para os clusters (círculos grandes)
    this.map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'patients',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'sum_patients'],
          '#f5f0ff', // Purple claro para <10
          10,
          '#d4c4eb', // Purple suave para 10-50
          51,
          '#b99de0', // Purple médio para 51-100
          101,
          '#9e76d6', // Purple para 101-500
          501,
          '#8350cc', // Purple escuro para 501-1000
          1001,
          '#6b3fa8'  // Purple profundo para 1000+
        ],
        'circle-radius': [
          'step',
          ['get', 'sum_patients'],
          16, // <10 pacientes
          10, 20, // 10-50 pacientes
          51, 25, // 51-100 pacientes
          101, 30, // 101-500 pacientes
          501, 35, // 501-1000 pacientes
          1001, 40  // 1000+ pacientes
        ],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff'
      }
    });

    // Camada para os números dos clusters
    this.map.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'patients',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'sum_patients'],
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': '#000000',
        'text-halo-width': 1
      }
    });

    // Camada para pontos individuais (não clustered)
    this.map.addLayer({
      id: 'unclustered-point',
      type: 'circle',
      source: 'patients',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': [
          'step',
          ['get', 'patientCount'],
          '#f5f0ff', // Purple claro para <10
          10,
          '#d4c4eb', // Purple suave para 10-50
          51,
          '#b99de0', // Purple médio para 51-100
          101,
          '#9e76d6', // Purple para 101-500
          501,
          '#8350cc', // Purple escuro para 501-1000
          1001,
          '#6b3fa8'  // Purple profundo para 1000+
        ],
        'circle-radius': [
          'step',
          ['get', 'patientCount'],
          14, // <10 pacientes
          10, 16, // 10-50 pacientes
          51, 18, // 51-100 pacientes
          101, 20, // 101-500 pacientes
          501, 22, // 501-1000 pacientes
          1001, 24  // 1000+ pacientes
        ],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff'
      }
    });

    // Camada para números nos pontos individuais
    this.map.addLayer({
      id: 'unclustered-point-count',
      type: 'symbol',
      source: 'patients',
      filter: ['!', ['has', 'point_count']],
      layout: {
        'text-field': ['get', 'patientCount'],
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 10
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': '#000000',
        'text-halo-width': 1
      }
    });

    // Eventos de clique nos clusters
    this.map.on('click', 'clusters', (e: any) => {
      const features = this.map.queryRenderedFeatures(e.point, {
        layers: ['clusters']
      });
      
      if (features.length > 0) {
        const feature = features[0];
        const coordinates = feature.geometry.coordinates.slice();
        const properties = feature.properties;
        
        // Criar popup para o cluster
        this.openClusterPopup({
          latitude: coordinates[1],
          longitude: coordinates[0],
          totalPatients: properties.sum_patients,
          pointCount: properties.point_count,
          clusterId: properties.cluster_id
        });
        
        // Não fazer zoom automático, deixar o usuário escolher
      }
    });

    // Eventos de clique nos pontos individuais
    this.map.on('click', 'unclustered-point', (e: any) => {
      const coordinates = e.features[0].geometry.coordinates.slice();
      const properties = e.features[0].properties;
      
      // Criar objeto de localização a partir das propriedades
      const location = {
        city: properties.city || '',
        state: properties.state || '',
        country: properties.country || '',
        latitude: coordinates[1],
        longitude: coordinates[0],
        patientCount: properties.patientCount
      };
      
      this.openPopup(location);
    });

    // Cursores para interatividade
    this.map.on('mouseenter', 'clusters', () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });
    
    this.map.on('mouseleave', 'clusters', () => {
      this.map.getCanvas().style.cursor = '';
    });
    
    this.map.on('mouseenter', 'unclustered-point', () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });
    
    this.map.on('mouseleave', 'unclustered-point', () => {
      this.map.getCanvas().style.cursor = '';
    });

    this.clustersInitialized = true;
  }

  /**
   * Atualiza o mapa com os dados de clustering
   */
  private updateMapWithClustering() {
    if (!this.map || !this.clustersInitialized) return;

    // Converter localizações para GeoJSON
    const geojsonData = this.createGeoJsonFromLocations(this.visibleLocations);
    
    // Atualizar a fonte de dados
    const source = this.map.getSource('patients');
    if (source) {
      source.setData(geojsonData);
    }

    // Ajustar o mapa para mostrar todas as localizações visíveis
    if (this.visibleLocations.length > 0) {
      this.fitMapToVisibleLocations();
    }
  }

  /**
   * Converte array de localizações para formato GeoJSON
   */
  private createGeoJsonFromLocations(locations: PatientLocation[]) {
    const features = locations.map(location => ({
      type: 'Feature',
      properties: {
        city: location.city || '',
        state: location.state || '',
        country: location.country || '',
        patientCount: location.patientCount
      },
      geometry: {
        type: 'Point',
        coordinates: [location.longitude, location.latitude]
      }
    }));

    return {
      type: 'FeatureCollection',
      features: features
    };
  }

  /**
   * Ajusta o mapa para mostrar todas as localizações visíveis
   */
  private fitMapToVisibleLocations() {
    if (!this.map || this.visibleLocations.length === 0) return;

    const bounds = createLngLatBounds();
    
    this.visibleLocations.forEach(location => {
      if (location.longitude && location.latitude) {
        bounds.extend([location.longitude, location.latitude]);
      }
    });
    
    if (!bounds.isEmpty()) {
      this.map.fitBounds(bounds, {
        padding: 50,
        duration: 1000,
        maxZoom: 9
      });
    }
  }
  
  /**
   * Limpa completamente todos os marcadores e clusters
   */
  private clearMarkers() {
    // Com clustering, não precisamos mais gerenciar marcadores individuais
    // A limpeza é feita automaticamente pela fonte de dados
    this.markers = {};
    
    // Limpar os dados da fonte de clustering se existe
    if (this.map && this.clustersInitialized) {
      const source = this.map.getSource('patients');
      if (source) {
        source.setData({
          type: 'FeatureCollection',
          features: []
        });
      }
    }
  }

  /**
   * Fecha o popup atualmente aberto, se houver
   */
  private closeCurrentPopup() {
    if (this.currentPopup) {
      this.currentPopup.remove();
      this.currentPopup = null;
    }
  }

  openPopup(location: PatientLocation) {
    if (!this.map) return;
    
    // Fechar popup atual antes de abrir novo
    this.closeCurrentPopup();
    
    this.selectedLocation = location;

    try {
      // Criar o conteúdo do popup
      const popupContent = document.createElement('div');
      
      // Formatar título com base nos dados disponíveis
      let title = '';
      if (location.city && location.state) {
        title = `${location.city}, ${location.state}`;
      } else if (location.city) {
        title = location.city;
      } else if (location.state) {
        title = location.state;
      } else if (location.country) {
        title = location.country;
      } else {
        title = 'Localização';
      }
      
      // Aplicar CSS inline para melhor aparência
      popupContent.innerHTML = `
        <div style="padding: 12px; min-width: 180px;">
          <h3 style="margin: 0 0 8px 0; font-size: 16px; font-weight: 600; color: #333; border-bottom: 1px solid #eee; padding-bottom: 8px;">
            ${title}
          </h3>
          <div style="display: flex; align-items: center; margin-bottom: 8px;">
            <div style="width: 12px; height: 12px; border-radius: 50%; background-color: ${this.getMarkerColor(location.patientCount)}; margin-right: 8px;"></div>
            <p style="margin: 0; font-size: 14px;">
              <strong>${location.patientCount}</strong> paciente${location.patientCount !== 1 ? 's' : ''}
            </p>
          </div>
          ${location.country ? `
          <p style="margin: 6px 0 0 0; font-size: 12px; color: #666;">
            ${location.country}
          </p>
          ` : ''}
          ${location.latitude && location.longitude ? `
          <p style="margin: 6px 0 0 0; font-size: 10px; color: #999; font-family: monospace;">
            ${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}
          </p>
          ` : ''}
          <button id="popup-zoom-btn" style="width: 100%; margin-top: 10px; padding: 6px 0; background-color: #6200ea; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 12px; font-weight: 500; transition: background-color 0.2s;">
            Ampliar mapa para esta área
          </button>
        </div>
      `;

      // Criar e exibir o popup
      const popup = createPopup({
        closeButton: true,
        closeOnClick: false
      })
        .setLngLat([location.longitude, location.latitude])
        .setDOMContent(popupContent)
        .addTo(this.map);
      
      // Armazenar referência do popup atual
      this.currentPopup = popup;
      
      // Adicionar evento de fechamento para limpar a referência
      popup.on('close', () => {
        if (this.currentPopup === popup) {
          this.currentPopup = null;
        }
      });
      
      // Adicionar evento ao botão dentro do popup
      setTimeout(() => {
        const zoomButton = document.getElementById('popup-zoom-btn');
        if (zoomButton) {
          zoomButton.addEventListener('click', () => {
            this.map.flyTo({
              center: [location.longitude, location.latitude],
              zoom: 12,
              duration: 1800,
              essential: true
            });
          });
        }
      }, 100);
    } catch (error) {
      console.error('Erro ao abrir popup:', error);
    }
  }

  /**
   * Abre popup para clusters usando o mesmo design do popup individual
   */
  openClusterPopup(clusterInfo: {
    latitude: number;
    longitude: number;
    totalPatients: number;
    pointCount: number;
    clusterId: string;
  }) {
    if (!this.map) return;

    // Fechar popup atual antes de abrir novo
    this.closeCurrentPopup();

    try {
      const popupContent = document.createElement('div');
      
      // Usar o mesmo design do popup individual
      popupContent.innerHTML = `
        <div style="padding: 12px; min-width: 180px;">
          <h3 style="margin: 0 0 8px 0; font-size: 16px; font-weight: 600; color: #333; border-bottom: 1px solid #eee; padding-bottom: 8px;">
            Grupo de Localizações
          </h3>
          <div style="display: flex; align-items: center; margin-bottom: 8px;">
            <div style="width: 12px; height: 12px; border-radius: 50%; background-color: ${this.getMarkerColor(clusterInfo.totalPatients)}; margin-right: 8px;"></div>
            <p style="margin: 0; font-size: 14px;">
              <strong>${clusterInfo.totalPatients}</strong> paciente${clusterInfo.totalPatients !== 1 ? 's' : ''}
            </p>
          </div>
          <p style="margin: 6px 0 0 0; font-size: 12px; color: #666;">
            ${clusterInfo.pointCount} localização${clusterInfo.pointCount !== 1 ? 'ões' : ''} agrupada${clusterInfo.pointCount !== 1 ? 's' : ''}
          </p>
          <p style="margin: 6px 0 0 0; font-size: 10px; color: #999; font-family: monospace;">
            ${clusterInfo.latitude.toFixed(6)}, ${clusterInfo.longitude.toFixed(6)}
          </p>
          <button id="popup-zoom-btn" style="width: 100%; margin-top: 10px; padding: 6px 0; background-color: #6200ea; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 12px; font-weight: 500; transition: background-color 0.2s;">
            Expandir para ver detalhes
          </button>
        </div>
      `;

      // Criar e exibir o popup
      const popup = createPopup({
        closeButton: true,
        closeOnClick: false
      })
        .setLngLat([clusterInfo.longitude, clusterInfo.latitude])
        .setDOMContent(popupContent)
        .addTo(this.map);
      
      // Armazenar referência do popup atual
      this.currentPopup = popup;
      
      // Adicionar evento de fechamento para limpar a referência
      popup.on('close', () => {
        if (this.currentPopup === popup) {
          this.currentPopup = null;
        }
      });
      
      // Adicionar evento ao botão (mesmo padrão do popup individual)
      setTimeout(() => {
        const zoomButton = document.getElementById('popup-zoom-btn');
        if (zoomButton) {
          zoomButton.addEventListener('click', () => {
            // Para clusters, expandir ao invés de só ampliar
            this.map.getSource('patients').getClusterExpansionZoom(clusterInfo.clusterId, (err: any, zoom: number) => {
              if (err) {
                // Se não conseguir expandir, só centralizar
                this.map.flyTo({
                  center: [clusterInfo.longitude, clusterInfo.latitude],
                  zoom: this.map.getZoom() + 2,
                  duration: 1800,
                  essential: true
                });
                return;
              }
              
              this.map.easeTo({
                center: [clusterInfo.longitude, clusterInfo.latitude],
                zoom: zoom + 1,
                duration: 1800
              });
              
              // Fechar o popup após expandir
              this.closeCurrentPopup();
            });
          });
        }
      }, 100);
    } catch (error) {
      console.error('Erro ao abrir popup do cluster:', error);
    }
  }

  getMarkerColor(count: number): string {
    if (count < 10) return '#f5f0ff';
    if (count <= 50) return '#d4c4eb';
    if (count <= 100) return '#b99de0';
    if (count <= 500) return '#9e76d6';
    if (count <= 1000) return '#8350cc';
    return '#6b3fa8';
  }

  recarregarDados() {
    this.fetchLocations();
  }
} 